import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { environment } from '../../environments/environment';
import { ToastService } from './toasts.service';
import { PlanogramService } from './planogram.service';
import { ProductOverrideService } from './product-override.service';
import { ModalService } from './modal.service';
import { Subject, Observable } from 'rxjs';
import { PlanogramFileExport } from '../models/planogram-file-export';
import { PlanogramLayout } from "../models/layout/planogram-layout";
import { ProductReference } from "../models/layout/product-reference";
import { LayoutService } from './layout.service';
import { FileDownloadService } from './file-download.service';
import { PlanogramStatusType } from '../models/planogram-status';

@Injectable({
  providedIn: 'root'
})
export class SignalRService {
  private signalUrl = environment.signalUrl;
  private hubConnected: boolean = false;
  private hubConnection: signalR.HubConnection;
  private tasksBusy: string[] = [];

  private downloadCompletedSubject: Subject<boolean> = new Subject<boolean>();
  public downloadCompleted$: Observable<boolean> = this.downloadCompletedSubject.asObservable();

  constructor(
    private toastService: ToastService,
    private planogramService: PlanogramService,
    private layoutService: LayoutService,
    private productOverrideService: ProductOverrideService,
    private modalService: ModalService,
    private fileDownloadService: FileDownloadService,
  ) {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(this.signalUrl)
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();
    this.registerSignalListeners();
    this.startSignalConnection();

    // Add connection state change listener
    this.hubConnection.onreconnecting((error) => {
      console.log('SignalR Reconnecting:', error);
      this.hubConnected = false;
      this.toastService.show('Reconnecting to server...', 
        { classname: 'bg-dark text-warning p-3 fs-5', delay: 3000 });
    });

    this.hubConnection.onreconnected((connectionId) => {
      console.log('SignalR Reconnected. ConnectionId:', connectionId);
      this.hubConnected = true;
      this.toastService.show('Connection restored', 
        { classname: 'bg-dark text-success p-3 fs-5', delay: 3000 });
    });
  }

  private registerSignalListeners() {
    this.hubConnection.on('ToastNotification', (message, type) => {
      this.toastService.show(message, { classname: `bg-dark text-${type} p-3 fs-5`, delay: 5000 })
    });

    this.hubConnection.on('TaskBusy', (task) => {
      this.tasksBusy.push(task);
    });

    this.hubConnection.on('TaskCompleted', (task) => {
      let taskIndex = this.tasksBusy.indexOf(task);
      if (taskIndex != -1) {
        this.tasksBusy.splice(taskIndex, 1);
      }
    });

    this.hubConnection.on('PlanogramExported', (planogramExport: PlanogramFileExport) => {
      this.downloadCompletedSubject.next(true);
      const binaryString = window.atob(planogramExport.data);
      const byteArray = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        byteArray[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([byteArray], { type: planogramExport.fileHeaders });
      this.fileDownloadService.triggerDownload(blob, planogramExport.fileName);
    });

    this.hubConnection.on('PlanogramLayout', (planogramLayout: PlanogramLayout) => {
      console.log('Received Layout', planogramLayout)
      let productRef = new Map<number,ProductReference>();
      Object.keys(planogramLayout.productReference).forEach(productId => {
        productRef.set(parseInt(productId), (planogramLayout.productReference as any)[productId])
      });
      if(planogramLayout.planogramOptions.stateId != null){
        this.planogramService.setPlanogramState(planogramLayout.planogramOptions.stateId)
      }
      planogramLayout.productReference = productRef;
      if(planogramLayout != null) {
        if(planogramLayout.unpackedProducts.length > 0){
          this.toastService.show(`Planogram has ${planogramLayout.unpackedProducts.length} products unpacked/underpacked`,
            { classname: `bg-dark text-danger p-3 fs-5`, delay: 5000 });
        }
        this.planogramService.setLevels(planogramLayout.planogramOptions.levels);
        this.productOverrideService.setOverrides(planogramLayout.planogramOptions);
        this.layoutService.setLayout(planogramLayout);
      }
    });

    this.hubConnection.on('PlanogramStatusUpdate', (status) => {
      this.planogramService.updateStatus(status);
    });
  }

  private async startSignalConnection(): Promise<void> {
    if (this.hubConnection.state === signalR.HubConnectionState.Disconnected) {
      try {
        await this.hubConnection.start();
        console.log("SignalR Connected.");
        this.hubConnected = true;
        
        this.hubConnection.onclose(async () => {
          this.hubConnected = false;
          console.log("SignalR Connection Closed");
          // Optional: Attempt to reconnect after connection closes
          await this.startSignalConnection();
        });
      }
      catch (err) {
        console.error(`SignalR Connection Error:`, err);
        this.hubConnected = false;
        
        // Retry connection after delay
        await new Promise((resolve) => setTimeout(resolve, 5000));
        return this.startSignalConnection();
      }
    } else if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
      this.hubConnected = true;
    }
  }

  async isHubConnected() {
    if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
      return true;
    }
    
    if (this.hubConnection.state === signalR.HubConnectionState.Disconnected) {
      await this.startSignalConnection();
    }
    
    return this.hubConnected;
  }

  async restartConnection(): Promise<void> {
    if (this.hubConnection.state !== signalR.HubConnectionState.Disconnected) {
      await this.hubConnection.stop();
    }
    await this.startSignalConnection();
  }

  async sendMessage(methodName: string, ...args: any[]) {
    const maxRetries = 3;
    let retryCount = 0;

    while (retryCount < maxRetries) {
      try {
        // Check connection and wait if needed
        const isConnected = await this.isHubConnected();
        
        if (!isConnected) {
          throw new Error('Connection not established');
        }

        // Verify we're still connected before sending
        if (this.hubConnection.state === signalR.HubConnectionState.Connected) {
          return await this.hubConnection.send(methodName, ...args);
        }
        
        throw new Error('Connection lost before sending');
      } catch (error) {
        retryCount++;
        console.warn(`SignalR send attempt ${retryCount} failed:`, error);
        
        if (retryCount < maxRetries) {
          // Wait 1 second between retries
          await new Promise(resolve => setTimeout(resolve, 1000));
          continue;
        }
        throw error;
      }
    }
  }

  isLoading(): boolean {
    return this.tasksBusy.length > 0;
  }

  async loadPlanogramState(stateId: string): Promise<void> {
    const planogramId = this.planogramService.getPlanogram();
    if (planogramId == null) return;

    this.layoutService.clearLayout();
    await this.sendMessage('LoadPlanogramState', stateId, planogramId);
  }

  async generatePlanogram(): Promise<void> {
    const planogramState = this.planogramService.currentStatus?.state
      ?? PlanogramStatusType.INITIALIZING;
    if (planogramState !== PlanogramStatusType.READY
      && planogramState !== PlanogramStatusType.LOADED) {
      return;
    }

    this.planogramService.updateStatusType(PlanogramStatusType.GENERATING);
    const planogramId = this.planogramService.getPlanogram();
    const stateId = this.planogramService.getPlanogramState() ?? '';
    const levels = this.planogramService.getLevels();
    const overrides = this.productOverrideService.getOverrides();
    const settings = this.planogramService.buildSettingsPayload();

    await this.sendMessage(
      'GeneratePlanogram',
      planogramId,
      stateId,
      levels,
      overrides,
      settings,
    );
  }
}